import React from 'react';
import { EXTENSIONS } from 'shared/constants/EXTENSIONS';
import { resolveExtensionFromMimeType } from 'shared/utils/fileExtensions';

/**
 * GeneratedExtensionIcon Component
 *
 * Dynamically generates file extension icons as inline SVG elements.
 *
 *
 * The icon consists of:
 * - A document-shaped background with a folded corner
 * - Color-coded by file category (CAD, images, documents, etc.)
 * - White text displaying the file extension (max 4 characters)
 */

interface ExtensionIconProps {
  fileType: string;
  fileExtension?: string;
  size?: number;
}

/**
 * Maps file extensions/categories to their display colors.
 * Colors are chosen to be visually distinct and match common file type conventions.
 */
const EXTENSION_COLORS: Record<string, string> = {
  // Documents
  pdf: '#E53935', // Red
  doc: '#1E88E5', // Blue
  docx: '#1E88E5',
  odt: '#1E88E5',
  rtf: '#1E88E5',

  // Spreadsheets
  xls: '#43A047', // Green
  xlsx: '#43A047',
  ods: '#43A047',
  csv: '#43A047',

  // Presentations
  ppt: '#F4511E', // Orange
  pptx: '#F4511E',
  odp: '#F4511E',

  // Images
  jpg: '#8E24AA', // Purple
  jpeg: '#8E24AA',
  png: '#8E24AA',
  gif: '#8E24AA',
  bmp: '#8E24AA',
  svg: '#8E24AA',
  webp: '#8E24AA',
  tiff: '#8E24AA',
  ico: '#8E24AA',

  // Video
  mp4: '#275F63', // Dark teal
  avi: '#275F63',
  mov: '#275F63',
  mkv: '#275F63',
  mpg: '#275F63',
  mpeg: '#275F63',
  wmv: '#275F63',
  flv: '#275F63',
  webm: '#275F63',
  '3gp': '#275F63',
  m4v: '#275F63',

  // Audio
  mp3: '#00897B', // Teal
  wav: '#00897B',
  flac: '#00897B',
  aac: '#00897B',
  ogg: '#00897B',
  wma: '#00897B',
  m4a: '#00897B',

  // Archives
  zip: '#6D4C41', // Brown
  rar: '#6D4C41',
  '7z': '#6D4C41',
  tar: '#6D4C41',
  gz: '#6D4C41',
  bz2: '#6D4C41',
  xz: '#6D4C41',

  // Code/Text
  txt: '#5E35B1', // Deep purple
  html: '#5E35B1',
  css: '#5E35B1',
  js: '#5E35B1',
  ts: '#5E35B1',
  jsx: '#5E35B1',
  tsx: '#5E35B1',
  json: '#5E35B1',
  xml: '#5E35B1',
  md: '#5E35B1',
  log: '#5E35B1',

  // CAD & Engineering - Construction Industry
  dwg: '#FB8C00', // Amber - AutoCAD Drawing
  dxf: '#FB8C00', // AutoCAD Drawing Exchange Format
  dwf: '#FB8C00', // Design Web Format (Autodesk)
  dwfx: '#FB8C00', // Design Web Format XPS
  dws: '#FB8C00', // AutoCAD Drawing Standards
  dwt: '#FB8C00', // AutoCAD Drawing Template

  // BIM (Building Information Modeling)
  ifc: '#D84315', // Deep orange - Industry Foundation Classes
  rvt: '#D84315', // Revit Project
  rfa: '#D84315', // Revit Family
  rte: '#D84315', // Revit Template
  rft: '#D84315', // Revit Family Template

  // 3D Modeling & Visualization
  skp: '#7B1FA2', // Purple - SketchUp
  '3ds': '#7B1FA2', // 3D Studio
  max: '#7B1FA2', // 3ds Max
  obj: '#7B1FA2', // Wavefront OBJ
  fbx: '#7B1FA2', // Filmbox (Autodesk)
  dae: '#7B1FA2', // Collada
  blend: '#7B1FA2', // Blender

  // Manufacturing & 3D Printing
  stl: '#00695C', // Dark teal - Stereolithography
  step: '#00695C', // Standard for Exchange of Product Data
  stp: '#00695C', // STEP file
  iges: '#00695C', // Initial Graphics Exchange Specification
  igs: '#00695C', // IGES file

  // GIS & Mapping (Geographic Information Systems)
  shp: '#558B2F', // Olive green - Shapefile
  kml: '#558B2F', // Keyhole Markup Language (Google Earth)
  kmz: '#558B2F', // Compressed KML
  gdb: '#558B2F', // Geodatabase
  mxd: '#558B2F', // ArcMap Document

  // Point Cloud & Survey Data
  las: '#4527A0', // Deep purple - LiDAR data
  laz: '#4527A0', // Compressed LAS
  e57: '#4527A0', // ASTM E57 3D imaging
  xyz: '#4527A0', // Point cloud ASCII
  pts: '#4527A0', // Point cloud data

  // Other Technical Formats
  dgn: '#C62828', // Dark red - MicroStation Design
  sat: '#AD1457', // Pink - ACIS model
  sldprt: '#1565C0', // Blue - SolidWorks Part
  sldasm: '#1565C0', // SolidWorks Assembly
  slddrw: '#1565C0', // SolidWorks Drawing
  prt: '#283593', // Indigo - Various CAD part files
  asm: '#283593', // Assembly file
  psm: '#AD1457', // Solid Edge Part
  par: '#AD1457', // Solid Edge Sheet Metal

  // Generic CAD
  cad: '#FB8C00', // Amber - Generic CAD file
};

/**
 * Fallback color for unknown file types
 */
const DEFAULT_COLOR = '#616161'; // Gray

/**
 * Gets color for a file extension, falling back to category-based color or default
 */
const getColorForExtension = (
  extension: string,
  mimeType: string
): string => {
  // Direct extension match
  const lowerExt = extension.toLowerCase();
  if (EXTENSION_COLORS[lowerExt]) {
    return EXTENSION_COLORS[lowerExt];
  }

  // MIME type category matching
  if (mimeType.startsWith('image/')) return EXTENSION_COLORS.jpg;
  if (mimeType.startsWith('video/')) return EXTENSION_COLORS.mp4;
  if (mimeType.startsWith('audio/')) return EXTENSION_COLORS.mp3;
  if (mimeType.includes(EXTENSIONS.PDF)) return EXTENSION_COLORS.pdf;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel'))
    return EXTENSION_COLORS.xls;
  if (mimeType.includes('word') || mimeType.includes('document'))
    return EXTENSION_COLORS.doc;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint'))
    return EXTENSION_COLORS.ppt;
  if (mimeType.includes('zip') || mimeType.includes('compressed'))
    return EXTENSION_COLORS.zip;
  if (mimeType.startsWith('text/')) return EXTENSION_COLORS.txt;

  return DEFAULT_COLOR;
};

/**
 * Formats extension text for display (max 4 characters, uppercase)
 */
const formatExtensionText = (extension: string): string => {
  const upper = extension.toUpperCase();
  return upper.length > 4 ? upper.substring(0, 4) : upper;
};

/**
 * Dynamically generates a file extension icon as an SVG.
 * This eliminates the need to load icons from the backend for each file type.
 */
export const GeneratedExtensionIcon: React.FC<ExtensionIconProps> = ({
  fileType,
  fileExtension,
  size = 48,
}) => {
  let extension = fileExtension || resolveExtensionFromMimeType(fileType);

  // If still no extension, try to guess from MIME type
  if (!extension && fileType) {
    const parts = fileType.split('/');
    if (parts.length === 2) {
      extension = parts[1].split(/[+.-]/)[0];
    }
  }

  const color = getColorForExtension(extension, fileType);
  const displayText = extension ? formatExtensionText(extension) : '?';

  // Calculate font size based on text length and icon size
  const baseFontSize = size * 0.26; // ~26% of icon size
  const fontSize =
    displayText.length > 3 ? baseFontSize * 0.85 : baseFontSize;

  // SVG viewBox is 40x41, scale to requested size
  const viewBoxWidth = 40;
  const viewBoxHeight = 41;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={{ display: 'block' }}
    >
      {/* Document shape with fold */}
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M33.0625 0.374756H0V40.3748H40V7.31225L33.0625 0.374756Z'
        fill={color}
      />

      {/* Extension text */}
      <text
        x='20'
        y='50%'
        dominantBaseline='middle'
        textAnchor='middle'
        fill='white'
        fontSize={fontSize}
        fontWeight='700'
        fontFamily='Arial, Helvetica, sans-serif'
        letterSpacing='-0.5'
      >
        {displayText}
      </text>
    </svg>
  );
};
